import React, { useState, useMemo, useEffect } from 'react';
import {
  Modal,
  Select,
  Table,
  Button,
  Space,
  Typography,
  Alert,
  Spin,
  Tag,
  App,
} from 'antd';
import { useActiveCustomers } from '../../hooks/useCustomers';
import {
  useDeliveriesEligibleForInvoice,
  useCreateSalesInvoiceFromDeliveries,
} from '../../hooks/useSalesInvoice';
import type { DeliveryEligibleForInvoice, DeliveryItemEligible } from '../../services/salesInvoice.service';
import type { ColumnsType } from 'antd/es/table';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import { useCurrency } from '@/contexts/CurrencyContext';

const { Text } = Typography;

interface CreateInvoiceFromDeliveriesModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (invoiceId: string) => void;
  /** Prefill when opening from a sales order (Sales Invoices navigation state) */
  initialCustomerId?: string | null;
  /** Narrow eligible deliveries to this sales order UUID */
  initialSalesOrderId?: string | null;
}

export const CreateInvoiceFromDeliveriesModal: React.FC<CreateInvoiceFromDeliveriesModalProps> = ({
  open,
  onClose,
  onSuccess,
  initialCustomerId,
  initialSalesOrderId,
}) => {
  const { message } = App.useApp();
  const { formatCurrency } = useCurrency();
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [salesOrderFilter, setSalesOrderFilter] = useState<string | undefined>(undefined);
  const [selectedDeliveryIds, setSelectedDeliveryIds] = useState<string[]>([]);
  const [departmentId, setDepartmentId] = useState<string | undefined>();

  const { data: customers = [], isLoading: loadingCustomers } = useActiveCustomers();
  const {
    data: deliveries = [],
    isLoading: loadingDeliveries,
    isError: deliveriesQueryFailed,
    error: deliveriesQueryError,
  } = useDeliveriesEligibleForInvoice(customerId, salesOrderFilter);

  const eligibleDeliveriesErrorText = (() => {
    const err = deliveriesQueryError as unknown;
    if (err && typeof err === 'object' && 'response' in err) {
      const data = (err as { response?: { data?: { message?: string; error?: string } } }).response?.data;
      if (typeof data?.message === 'string') return data.message;
      if (typeof data?.error === 'string') return data.error;
    }
    if (err instanceof Error) return err.message;
    return 'Could not load deliveries. Check your connection and try again.';
  })();

  useEffect(() => {
    if (!open) return;
    if (initialCustomerId) setCustomerId(initialCustomerId);
    setSalesOrderFilter(initialSalesOrderId || undefined);
  }, [open, initialCustomerId, initialSalesOrderId]);

  /** Eligible API should already omit fully invoiced DNs; keep a client-side guard. */
  const invoiceableDeliveries = useMemo(
    () =>
      deliveries.filter((d) =>
        (d.items ?? []).some((i) => Number((i as DeliveryItemEligible).available_quantity ?? 0) > 0)
      ),
    [deliveries]
  );

  useEffect(() => {
    if (!open || selectedDeliveryIds.length === 0) return;
    const selected = invoiceableDeliveries.filter((d) => selectedDeliveryIds.includes(d.id));
    const soDept = selected
      .map((d) => (d.salesOrder as { department_id?: string | null } | undefined)?.department_id)
      .find((id) => id && String(id).trim());
    if (soDept && !departmentId) {
      setDepartmentId(String(soDept).trim());
    }
  }, [open, selectedDeliveryIds, invoiceableDeliveries, departmentId]);

  const createMutation = useCreateSalesInvoiceFromDeliveries();

  const hasDeliveredNotes = !deliveriesQueryFailed && invoiceableDeliveries.length > 0;

  const totalAvailableLines = useMemo(() => {
    let count = 0;
    for (const d of invoiceableDeliveries) {
      if (!selectedDeliveryIds.includes(d.id)) continue;
      for (const i of d.items ?? []) {
        const avail = (i as DeliveryItemEligible).available_quantity ?? 0;
        if (avail > 0) count++;
      }
    }
    return count;
  }, [invoiceableDeliveries, selectedDeliveryIds]);

  const selectedInvoicePreview = useMemo(() => {
    let amount = 0;
    let currency: string | undefined;
    for (const d of invoiceableDeliveries) {
      if (!selectedDeliveryIds.includes(d.id)) continue;
      const dnAmt = Number(d.invoiceable_amount);
      if (Number.isFinite(dnAmt)) {
        amount += dnAmt;
      } else {
        for (const i of d.items ?? []) {
          const lineAmt = Number((i as DeliveryItemEligible).invoiceable_amount ?? 0);
          if (Number.isFinite(lineAmt)) amount += lineAmt;
        }
      }
      const c = d.salesOrder?.currency?.trim();
      if (c) currency = c;
    }
    return { amount: Math.round(amount * 100) / 100, currency };
  }, [invoiceableDeliveries, selectedDeliveryIds]);

  const handleClose = () => {
    setCustomerId(null);
    setSalesOrderFilter(undefined);
    setSelectedDeliveryIds([]);
    setDepartmentId(undefined);
    onClose();
  };

  const handleCreate = async () => {
    if (!customerId || selectedDeliveryIds.length === 0) return;
    if (!departmentId?.trim()) {
      message.warning('Department is required');
      return;
    }
    try {
      const invoice = await createMutation.mutateAsync({
        customerId,
        deliveryNoteIds: selectedDeliveryIds,
        departmentId: departmentId.trim(),
      });
      handleClose();
      onSuccess(invoice.id);
    } catch {
      // Error already shown by mutation
    }
  };

  const deliveryColumns: ColumnsType<DeliveryEligibleForInvoice> = [
    {
      title: 'Delivery #',
      dataIndex: 'delivery_note_number',
      key: 'delivery_note_number',
      width: 140,
    },
    {
      title: 'Date',
      dataIndex: 'delivery_date',
      key: 'delivery_date',
      width: 110,
      render: (v: string) => (v ? new Date(v).toLocaleDateString() : '-'),
    },
    {
      title: 'SO #',
      key: 'so_number',
      width: 120,
      render: (_: unknown, r: DeliveryEligibleForInvoice) => r.salesOrder?.sales_order_number ?? '-',
    },
    {
      title: 'Lines (available)',
      key: 'lines_available',
      width: 120,
      render: (_: unknown, r: DeliveryEligibleForInvoice) => {
        const items = r.items ?? [];
        const withAvail = items.filter((i) => ((i as DeliveryItemEligible).available_quantity ?? 0) > 0);
        return <Tag color="blue">{withAvail.length} line(s)</Tag>;
      },
    },
    {
      title: 'Invoice amount',
      key: 'invoiceable_amount',
      width: 130,
      align: 'right',
      render: (_: unknown, r: DeliveryEligibleForInvoice) => {
        const amt = Number(r.invoiceable_amount ?? 0);
        if (!(amt > 0)) return <Text type="secondary">—</Text>;
        const cur = r.salesOrder?.currency?.trim();
        return formatCurrency(amt, cur || undefined);
      },
    },
  ];

  const rowSelection = {
    selectedRowKeys: selectedDeliveryIds,
    onChange: (keys: React.Key[]) => setSelectedDeliveryIds(keys as string[]),
    getCheckboxProps: (record: DeliveryEligibleForInvoice) => {
      const hasAvail = (record.items ?? []).some(
        (i) => ((i as DeliveryItemEligible).available_quantity ?? 0) > 0
      );
      return { disabled: !hasAvail };
    },
  };

  const canCreate =
    customerId && selectedDeliveryIds.length > 0 && totalAvailableLines > 0 && !!departmentId?.trim();

  return (
    <Modal
      title="Create Invoice from Deliveries"
      open={open}
      onCancel={handleClose}
      footer={null}
      width={960}
      destroyOnClose
      styles={{ body: { maxHeight: 'calc(100vh - 120px)', overflowY: 'auto' } }}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <div>
          <Text strong>Customer</Text>
          {salesOrderFilter ? (
            <div style={{ marginTop: 4 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Showing only delivery notes for the customer order you opened this from. Use Tax Invoices → Create
                Invoice from Deliveries without the order shortcut to see all customers’ notes.
              </Text>
            </div>
          ) : null}
          <div style={{ marginTop: 4 }}>
            <Select
              placeholder="Select customer"
              value={customerId ?? undefined}
              onChange={setCustomerId}
              style={{ width: '100%' }}
              loading={loadingCustomers}
              showSearch
              optionFilterProp="label"
              options={customers.map((c: { id: string; customer_name?: string; name?: string; customer_code?: string }) => {
                const name = c.customer_name ?? c.name ?? c.id;
                const label = c.customer_code ? `${name} (${c.customer_code})` : name;
                return { value: c.id, label };
              })}
            />
          </div>
        </div>

        {customerId && (
          <>
            <div>
              <Text strong>Deliveries (select one or more)</Text>
              <div style={{ marginTop: 4 }}>
                {loadingDeliveries ? (
                  <Spin tip="Loading deliveries..." />
                ) : deliveriesQueryFailed ? (
                  <Alert
                    type="error"
                    showIcon
                    message="Failed to load deliveries"
                    description={eligibleDeliveriesErrorText}
                  />
                ) : !hasDeliveredNotes ? (
                  <Alert
                    type="info"
                    message="No deliveries left to invoice"
                    description={
                      salesOrderFilter
                        ? 'No delivered notes with remaining quantity for this customer order. Fully invoiced deliveries are hidden. Confirm the delivery is Delivered, or reopen from Tax Invoices without the order shortcut.'
                        : 'There are no delivered notes with remaining quantity for this customer. Fully invoiced deliveries are hidden. Mark a delivery complete first, or verify the customer matches the delivery note / customer order.'
                    }
                  />
                ) : (
                  <Table<DeliveryEligibleForInvoice>
                    rowKey="id"
                    size="small"
                    pagination={false}
                    rowSelection={rowSelection}
                    columns={deliveryColumns}
                    dataSource={invoiceableDeliveries}
                    scroll={{ y: 420 }}
                  />
                )}
              </div>
            </div>

            {selectedDeliveryIds.length > 0 && totalAvailableLines === 0 && (
              <Alert type="warning" message="Selected deliveries have no remaining quantity to invoice." />
            )}

            {selectedDeliveryIds.length > 0 && totalAvailableLines > 0 && (
              <Alert
                type="info"
                showIcon
                message={
                  <span>
                    Invoice amount (incl. VAT):{' '}
                    <Text strong>
                      {formatCurrency(
                        selectedInvoicePreview.amount,
                        selectedInvoicePreview.currency || undefined
                      )}
                    </Text>
                    {selectedDeliveryIds.length > 1
                      ? ` · ${selectedDeliveryIds.length} deliveries · ${totalAvailableLines} line(s)`
                      : ` · ${totalAvailableLines} line(s)`}
                  </span>
                }
              />
            )}

            {selectedDeliveryIds.length > 0 && (
              <div>
                <Text strong>
                  Department <Text type="danger">*</Text>
                </Text>
                <div style={{ marginTop: 4 }}>
                  <DepartmentSelect
                    value={departmentId}
                    onChange={(value) => setDepartmentId(value || undefined)}
                    style={{ width: '100%' }}
                    placeholder="Select department"
                  />
                </div>
              </div>
            )}

            {canCreate && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <Button onClick={handleClose}>Cancel</Button>
                <Button
                  type="primary"
                  onClick={handleCreate}
                  loading={createMutation.isPending}
                >
                  Create Invoice
                </Button>
              </div>
            )}
          </>
        )}
      </Space>
    </Modal>
  );
};
